import {
	IconArrowTurnDownOutline18 as ArrowTurnDown,
	IconPlusOutline18 as Plus,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import type { FieldRule } from "@productlane/lib"
import { MAX_FALLBACK_STEPS } from "@productlane/lib"
import { Button, IconButton } from "@productlane/ui"

import type { StepMode } from "../model/fallback-step"
import type { RuleEditorProps } from "./rule-editor-props"
import { OptionSelect } from "../inputs/option-select"
import {
	addStep,
	applySteps,
	chainSteps,
	removeStep,
	setStep,
	STEP_MODES,
} from "../model/fallback-step"
import { MODE_LABELS } from "../model/rule-modes"
import { agentTypesFor, isContainerKind } from "../model/typed-value"
import { ChainStep } from "./chain-step"

type ChainRule = Extract<FieldRule, { mode: "ref" | "template" }>

function stepName(path: string, mode: StepMode, index: number): string {
	if (index === 0) return `rule-${path}`
	const prefix = mode === "fixed" ? "fallback-fixed" : "fallback"
	return `${prefix}-${path}-${index - 1}`
}

/** A ref field holds one reference, so a token can only live in a template. */
function asTemplate(rule: ChainRule): ChainRule {
	return {
		mode: "template",
		template: "",
		...(rule.cast && { cast: rule.cast }),
		...(rule.fallbacks && { fallbacks: rule.fallbacks }),
	}
}

/**
 * Step 1 is the rule's own value; the rest are its fallbacks. The chain runs in
 * order and stops at the first step that resolves.
 */
export function ChainEditor({
	field,
	rule,
	onChange,
	refOptions,
	refsState,
	onRetryRefs,
	refBudgetLeft,
	knownKeys,
}: Omit<RuleEditorProps, "rule"> & { rule: ChainRule }) {
	const steps = chainSteps(rule)
	// The field decides, not the cast: an object argument gets the roomy editor even
	// before the cast is switched on, since its value is JSON either way.
	const writesJson =
		rule.cast === "json" ||
		(field.kinds.length > 0 && field.kinds.every(isContainerKind))

	const agentTypes = agentTypesFor(field)
	// An object argument is never the one value a typed token reads, so the token
	// starts on a property of it: the first the schema declares, or a name to replace.
	const holdsProperties =
		field.kinds.length > 0 && field.kinds.every((kind) => kind === "object")
	const agentProperty = holdsProperties
		? (knownKeys?.[0] ?? "property")
		: undefined
	const last = steps.at(-1)
	const canAdd =
		last?.mode !== "fixed" &&
		steps.length <= MAX_FALLBACK_STEPS &&
		refBudgetLeft > 0

	return (
		<div className="mt-1.5 space-y-2">
			{/* No indent: a fallback is another step of the same field, so its box lines
			    up with the first one rather than reading as a nested thing. */}
			{steps.map((step, index) => (
				<div key={index}>
					{index > 0 ? (
						<div className="mb-1 flex items-center justify-between gap-2">
							<span className="flex items-center gap-1.5 text-xs text-tertiary">
								{/* Turns down out of the box above, which is the order it runs in. */}
								<ArrowTurnDown className="h-3.5 w-3.5 flex-none" />
								Only when the steps above are unset
							</span>
							<span className="flex flex-none items-center gap-1">
								<OptionSelect
									size="xs"
									align="end"
									triggerClassName="min-w-32"
									value={step.mode}
									options={STEP_MODES.filter(
										// A fixed value ends the chain, so it can only sit last.
										(mode) => mode !== "fixed" || index === steps.length - 1,
									).map((mode) => ({ value: mode, label: MODE_LABELS[mode] }))}
									onSelect={(mode: StepMode) =>
										onChange(setStep(rule, index, { mode }), mode === "fixed")
									}
								/>
								<IconButton
									Icon={XIcon}
									variant="ghost"
									aria-label={`Remove step ${index + 1}`}
									onClick={() => onChange(removeStep(rule, index))}
								/>
							</span>
						</div>
					) : null}

					<ChainStep
						name={stepName(field.path, step.mode, index)}
						step={step}
						writesJson={writesJson}
						cast={rule.cast}
						agentTypes={agentTypes}
						agentProperty={agentProperty}
						knownKeys={knownKeys}
						refOptions={refOptions}
						refsState={refsState}
						onRetryRefs={onRetryRefs}
						onChange={(value, save) =>
							onChange(setStep(rule, index, { value }), save)
						}
						onPickAgent={(template) =>
							onChange(
								index === 0
									? applySteps(asTemplate(rule), [
											{ mode: "template", value: template },
											...steps.slice(1),
										])
									: setStep(rule, index, { mode: "template", value: template }),
							)
						}
					/>
				</div>
			))}

			{canAdd ? (
				<Button
					variant="dashed"
					size="xs"
					Icon={Plus}
					onClick={() => onChange(addStep(rule), false)}
				>
					Add fallback
				</Button>
			) : null}
		</div>
	)
}
